import { InputState, TouchState, MouseState, Vector2 } from '../types/game.types';
import { MobileInputHandler } from './MobileInputHandler';

export class InputManager {
  private keys: { [key: string]: boolean } = {};
  private previousKeys: { [key: string]: boolean } = {};
  private touchState: TouchState;
  private mouseState: MouseState;
  private canvas: HTMLCanvasElement | null = null;
  private mobileHandler: MobileInputHandler;

  constructor() {
    this.touchState = {
      active: false,
      position: { x: 0, y: 0 },
      startPosition: { x: 0, y: 0 },
      gesture: 'none'
    };
    
    this.mouseState = {
      position: { x: 0, y: 0 },
      buttons: {}
    };
    
    this.mobileHandler = MobileInputHandler.getInstance();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));
    
    // Touch events
    window.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    window.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    window.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
    
    // Mouse events
    window.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    
    // Prevent default behaviors
    window.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Focus handling to prevent key states from getting stuck
    window.addEventListener('blur', this.onWindowBlur.bind(this));
    window.addEventListener('focus', this.onWindowFocus.bind(this));
  }

  public setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.mobileHandler.setCanvas(canvas);
  }

  private onKeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    this.keys[event.code] = true;
  }

  private onKeyUp(event: KeyboardEvent): void {
    event.preventDefault();
    this.keys[event.code] = false;
  }

  private onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const position = this.calculateTouchPosition(touch);
      
      this.touchState = {
        active: true,
        position,
        startPosition: { ...position },
        gesture: 'none'
      };
      
      // Handle mobile controls
      this.mobileHandler.handleTouchStart(touch);
    }
  }

  private onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    
    if (event.touches.length > 0 && this.touchState.active) {
      const touch = event.touches[0];
      const position = this.calculateTouchPosition(touch);
      
      this.touchState.position = position;
      
      // Detect swipe gesture
      const deltaX = position.x - this.touchState.startPosition.x;
      const deltaY = position.y - this.touchState.startPosition.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance > 30) { // Minimum swipe distance
        this.touchState.gesture = 'swipe';
      }
      
      // Handle mobile controls
      this.mobileHandler.handleTouchMove(touch);
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    
    if (this.touchState.active) {
      // Determine final gesture
      const deltaX = this.touchState.position.x - this.touchState.startPosition.x;
      const deltaY = this.touchState.position.y - this.touchState.startPosition.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance < 15) { // Increased tap threshold for better mobile detection
        this.touchState.gesture = 'tap';
      }
      
      // Handle mobile controls
      this.mobileHandler.handleTouchEnd();
      
      // Keep the gesture active longer for better detection
      setTimeout(() => {
        this.touchState = {
          active: false,
          position: this.touchState.position, // Keep last position
          startPosition: this.touchState.startPosition, // Keep start position  
          gesture: 'none'
        };
      }, 150); // Increased delay to 150ms
    }
  }

  private onMouseDown(event: MouseEvent): void {
    this.mouseState.buttons[event.button] = true;
    this.mouseState.position = this.calculateMousePosition(event);
  }

  private onMouseUp(event: MouseEvent): void {
    this.mouseState.buttons[event.button] = false;
    this.mouseState.position = this.calculateMousePosition(event);
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouseState.position = this.calculateMousePosition(event);
  }

  private onWindowBlur(): void {
    // Clear all key states when window loses focus
    this.keys = {};
    this.previousKeys = {};
  }

  private onWindowFocus(): void {
    // Reset input states when window gains focus
    this.keys = {};
    this.previousKeys = {};
  }

  private calculateTouchPosition(touch: Touch): Vector2 {
    if (!this.canvas) {
      return { x: touch.clientX, y: touch.clientY };
    }
    
    const rect = this.canvas.getBoundingClientRect();
    
    // Calculate relative position within the canvas display area
    const relativeX = (touch.clientX - rect.left) / rect.width;
    const relativeY = (touch.clientY - rect.top) / rect.height;
    
    // Convert to internal canvas coordinates
    return {
      x: relativeX * this.canvas.width,
      y: relativeY * this.canvas.height
    };
  }

  private calculateMousePosition(event: MouseEvent): Vector2 {
    if (!this.canvas) {
      return { x: event.clientX, y: event.clientY };
    }
    
    const rect = this.canvas.getBoundingClientRect();
    
    // Calculate relative position within the canvas display area
    const relativeX = (event.clientX - rect.left) / rect.width;
    const relativeY = (event.clientY - rect.top) / rect.height;
    
    // Convert to internal canvas coordinates
    return {
      x: relativeX * this.canvas.width,
      y: relativeY * this.canvas.height
    };
  }

  public update(): void {
    // Debug logging for P key BEFORE update
    if (this.keys['KeyP']) {
      console.log('InputManager.update() BEFORE - KeyP current:', !!this.keys['KeyP'], 'previous:', !!this.previousKeys['KeyP']);
    }
    
    // Store previous key states for edge detection
    this.previousKeys = { ...this.keys };
    
    // Debug logging for P key AFTER update
    if (this.keys['KeyP']) {
      console.log('InputManager.update() AFTER - KeyP current:', !!this.keys['KeyP'], 'previous:', !!this.previousKeys['KeyP']);
    }
  }

  public getInputState(): InputState {
    const baseInput = {
      keys: { ...this.keys },
      touch: { ...this.touchState },
      mouse: { ...this.mouseState }
    };
    
    // Process mobile input if on mobile device
    return this.mobileHandler.processTouchInput(baseInput);
  }
  
  public setupGameControls(gameType: string): void {
    this.mobileHandler.setupGameControls(gameType);
  }
  
  public getMobileHandler(): MobileInputHandler {
    return this.mobileHandler;
  }

  // Helper methods for common input checks
  public isKeyPressed(keyCode: string): boolean {
    return !!this.keys[keyCode];
  }

  public wasKeyPressed(keyCode: string): boolean {
    return !!this.previousKeys[keyCode];
  }

  public isKeyJustPressed(keyCode: string): boolean {
    const result = this.isKeyPressed(keyCode) && !this.wasKeyPressed(keyCode);
    if (keyCode === 'KeyP' && (this.isKeyPressed(keyCode) || this.wasKeyPressed(keyCode))) {
      console.log('isKeyJustPressed(KeyP) - current:', this.isKeyPressed(keyCode), 'previous:', this.wasKeyPressed(keyCode), 'result:', result);
    }
    return result;
  }

  public isKeyJustReleased(keyCode: string): boolean {
    return !this.isKeyPressed(keyCode) && this.wasKeyPressed(keyCode);
  }

  // Movement helpers (for arrow keys and WASD)
  public getMovementVector(): Vector2 {
    const movement = { x: 0, y: 0 };
    
    // Arrow keys
    if (this.isKeyPressed('ArrowLeft') || this.isKeyPressed('KeyA')) {
      movement.x -= 1;
    }
    if (this.isKeyPressed('ArrowRight') || this.isKeyPressed('KeyD')) {
      movement.x += 1;
    }
    if (this.isKeyPressed('ArrowUp') || this.isKeyPressed('KeyW')) {
      movement.y -= 1;
    }
    if (this.isKeyPressed('ArrowDown') || this.isKeyPressed('KeyS')) {
      movement.y += 1;
    }
    
    return movement;
  }

  // Touch gesture helpers
  public getTouchGesture(): 'none' | 'tap' | 'swipe' | 'hold' {
    return this.touchState.gesture;
  }

  public getSwipeDirection(): Vector2 {
    if (this.touchState.gesture === 'swipe') {
      const deltaX = this.touchState.position.x - this.touchState.startPosition.x;
      const deltaY = this.touchState.position.y - this.touchState.startPosition.y;
      
      const magnitude = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (magnitude > 0) {
        return { x: deltaX / magnitude, y: deltaY / magnitude };
      }
    }
    
    return { x: 0, y: 0 };
  }

  public isTouchActive(): boolean {
    return this.touchState.active;
  }

  public getTouchPosition(): Vector2 {
    return { ...this.touchState.position };
  }

  // Mouse helpers
  public isMouseButtonPressed(button: number = 0): boolean {
    return !!this.mouseState.buttons[button];
  }

  public getMousePosition(): Vector2 {
    return { ...this.mouseState.position };
  }

  // Virtual button mapping for mobile
  public checkVirtualButton(x: number, y: number, width: number, height: number): boolean {
    if (!this.touchState.active) return false;
    
    const touch = this.touchState.position;
    return touch.x >= x && touch.x <= x + width && 
           touch.y >= y && touch.y <= y + height;
  }

  // Cleanup
  public destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('blur', this.onWindowBlur);
    window.removeEventListener('focus', this.onWindowFocus);
  }
}